import { AbortError } from '@/domains/common/abort';
import { type RawDataResponse, RawDataOutputSchema, type RouteDef } from '@/domains/common/api';
import { isImpersonationSession } from '@/domains/common/impersonation.client';
import { logError, logMessage } from '@/domains/common/logger.client';
import { parseJsonSafe } from '@/domains/common/parse-json-safe';
import { showToast } from '@/domains/common/toast';

// Front-end caller. JSON routes resolve to TOutput; raw-data routes
// (def declares `output: rawDataOutput`) resolve to the raw `Response`.
// Stream routes go through `streamApi`.
export type ApiReturn<TOutput> = TOutput extends RawDataResponse ? Response : TOutput;

export interface ResumableOptions {
  // How many times to retry a dropped read before giving up. Default 5.
  maxAttempts?: number;
  // Initial backoff in milliseconds; exponential (×1.5 per attempt). Default 1000.
  backoffMs?: number;
}

interface CallArgs<TParams, TQuery, TBody> {
  params?: TParams;
  query?: TQuery;
  // Pass `FormData` for multipart routes; the helper skips JSON
  // serialization and drops the Content-Type header so the browser
  // sets the multipart boundary itself.
  body?: TBody | FormData;
  signal?: AbortSignal;
  // Called once if the request fails, BEFORE the error rethrows. Use
  // it to flip loading state off / set an error UI from the call site
  // without a try/catch/finally wrapper. Errors thrown by the fallback
  // itself are swallowed so the original error always propagates.
  // Skipped on AbortError — a caller-initiated abort is not a failure.
  gracefulFallback?: (err: unknown) => void;
  // Opt-in to resumable SSE: on a non-abort read error the helper
  // re-issues the request with `?since=<lastEventI>` and continues
  // yielding to the same consumer. Only effective on streamApi. A
  // synthetic `{ kind: 'gap', count: null }` event is yielded after
  // any reconnect that loses event continuity — the consumer must
  // include `gap` in its event union to receive it.
  resumable?: ResumableOptions;
}

function runGracefulFallback(fn: ((err: unknown) => void) | undefined, err: unknown): void {
  if (AbortError.is(err)) return;
  try {
    fn?.(err);
  } catch (fallbackErr) {
    logMessage('[api-client] gracefulFallback threw; ignoring', { err: fallbackErr });
  }
}

export class ApiCallError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(typeof body === 'string' ? body : `HTTP ${status}`);
  }
}

const BACK_OFFICE_API_PREFIX = '/api/back-office/';
const BACK_OFFICE_PAGE_PREFIX = '/back-office';

// Raw-data and SSE responses carry the body as text, so the code has to be read
// out of either shape.
function errorCode(body: unknown): string | null {
  const parsed = typeof body === 'string' ? parseJsonSafe<{ error?: unknown }>(body) : body;
  if (typeof parsed !== 'object' || parsed === null) return null;
  const code = (parsed as { error?: unknown }).error;
  return typeof code === 'string' ? code : null;
}

// Signing out in one tab and carrying on in another leaves the second tab
// asking with no session. That is the session's lifecycle, not a fault, so the
// tab goes to its sign-in page instead of throwing.
const STAFF_LOGIN_PATH = '/login';
const LOGIN_PATH_FOR_CODE: Record<string, string> = {
  unauthenticated: STAFF_LOGIN_PATH,
  back_office_admin_session_required: `${BACK_OFFICE_PAGE_PREFIX}/login`,
};

function loginPathForSessionExpiry(err: unknown): string | null {
  if (!(err instanceof ApiCallError) || err.status !== 401) return null;
  const code = errorCode(err.body);
  return code ? (LOGIN_PATH_FOR_CODE[code] ?? null) : null;
}

function bounceToLogin(loginPath: string): void {
  if (typeof window === 'undefined') return;
  // Only the staff sign-in takes a redirect_to; the back-office one lands on
  // the console root.
  if (loginPath !== STAFF_LOGIN_PATH) {
    window.location.href = loginPath;
    return;
  }
  const current = window.location.pathname + window.location.search;
  window.location.href = `${loginPath}?redirect_to=${encodeURIComponent(current)}`;
}

// An ordinary app page asking for back-office data is the case worth knowing
// about — a route or a shared client leaking an admin call out of the console —
// and it was invisible while every expired-session 401 was also being reported.
function reportBackOfficeCallFromAppPage(path: string): void {
  if (typeof window === 'undefined') return;
  if (!path.startsWith(BACK_OFFICE_API_PREFIX)) return;
  if (window.location.pathname.startsWith(BACK_OFFICE_PAGE_PREFIX)) return;
  logError(new Error('back-office endpoint called from a non-back-office page'), {
    api_path: path,
    page_path: window.location.pathname,
  });
}

// Thrown when an SSE stream emits a framework `event: error` frame
// (the server's generator caught an uncaught exception). Surfaces to
// the consumer's `for await` as a thrown error so route event schemas
// don't have to carry a framework-owned `kind: 'error'` variant.
export class StreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StreamError';
  }
}

function buildUrl(path: string, params: Record<string, unknown> | undefined): string {
  if (!params) return path;
  return Object.entries(params).reduce<string>(
    (out, [k, v]) => out.replace(`[${k}]`, encodeURIComponent(String(v))),
    path,
  );
}

// Impersonation sessions are read-only. Refuse the mutation before it leaves
// the browser: toast the user, report to Sentry, and throw an AbortError — an
// error the callers' catch paths and Sentry both already treat as non-failure,
// so the block itself is reported exactly once. SSE streams stay allowed;
// streamApi deliberately does not call this.
function throwIfImpersonationWrite(method: string, path: string): void {
  if (method === 'GET' || !isImpersonationSession()) return;
  showToast(`Detected a ${method} request while impersonating — this action is blocked.`, {
    level: 'error',
  });
  logError(new Error('impersonation write blocked'), { method, path });
  throw new AbortError('impersonation_write_blocked');
}

export async function callApi<TParams, TQuery, TBody, TOutput>(
  def: RouteDef<TParams, TQuery, TBody, TOutput>,
  args: CallArgs<TParams, TQuery, TBody> = {},
): Promise<ApiReturn<TOutput>> {
  throwIfImpersonationWrite(def.method, def.path);
  reportBackOfficeCallFromAppPage(def.path);
  try {
    const url = new URL(
      buildUrl(def.path, args.params as Record<string, unknown> | undefined),
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
    );
    if (args.query) {
      for (const [k, v] of Object.entries(args.query)) {
        if (v == null) continue;
        url.searchParams.set(k, String(v));
      }
    }

    const isRawData = def.output instanceof RawDataOutputSchema;
    const init: RequestInit = {
      method: def.method,
      headers: { Accept: isRawData ? '*/*' : 'application/json' },
      signal: args.signal,
    };
    if (args.body !== undefined) {
      if (args.body instanceof FormData) {
        // Browser sets multipart Content-Type with the boundary itself.
        init.body = args.body;
      } else {
        init.headers = { ...init.headers, 'Content-Type': 'application/json' };
        init.body = JSON.stringify(args.body);
      }
    }

    const res = await fetch(url.toString(), init);

    if (isRawData) {
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new ApiCallError(res.status, text || 'request_failed');
      }
      return res as ApiReturn<TOutput>;
    }

    const text = await res.text();
    const json: unknown = text.length > 0 ? (parseJsonSafe(text) ?? text) : null;

    if (!res.ok) throw new ApiCallError(res.status, json);
    return json as ApiReturn<TOutput>;
  } catch (err) {
    const loginPath = loginPathForSessionExpiry(err);
    if (loginPath) {
      bounceToLogin(loginPath);
      throw new AbortError('unauthenticated');
    }
    runGracefulFallback(args.gracefulFallback, err);
    throw err;
  }
}

// SSE consumer for routes declared with an `events` schema. `data` is
// an AsyncIterable<SseEvent<TOutput>>; `stop()` aborts the underlying
// fetch. No runtime parsing — trust the def's TOutput typing.
export interface SseEvent<T> {
  event: string | null;
  data: T;
}

export interface StreamHandle<TOutput> {
  data: AsyncIterable<SseEvent<TOutput>>;
  stop: () => void;
}

export function streamApi<TParams, TQuery, TBody, TOutput>(
  def: RouteDef<TParams, TQuery, TBody, TOutput>,
  args: CallArgs<TParams, TQuery, TBody> = {},
): StreamHandle<TOutput> {
  reportBackOfficeCallFromAppPage(def.path);
  const ac = new AbortController();
  if (args.signal) {
    if (args.signal.aborted) ac.abort();
    else args.signal.addEventListener('abort', () => ac.abort(), { once: true });
  }
  return {
    data: args.resumable
      ? streamGenResumable(def, { ...args, signal: ac.signal }, args.resumable)
      : streamGen(def, { ...args, signal: ac.signal }),
    stop: () => ac.abort(new AbortError()),
  };
}

interface ParsedFrame<T> {
  event: string | null;
  data: T;
  i: number | null;
}

async function* rawStreamGen<TParams, TQuery, TBody, TOutput>(
  def: RouteDef<TParams, TQuery, TBody, TOutput>,
  args: CallArgs<TParams, TQuery, TBody>,
  extraQuery: Record<string, string> | null,
): AsyncGenerator<ParsedFrame<TOutput>, void, void> {
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  try {
    const url = new URL(
      buildUrl(def.path, args.params as Record<string, unknown> | undefined),
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
    );
    if (args.query) {
      for (const [k, v] of Object.entries(args.query)) {
        if (v == null) continue;
        url.searchParams.set(k, String(v));
      }
    }
    if (extraQuery) {
      for (const [k, v] of Object.entries(extraQuery)) {
        url.searchParams.set(k, v);
      }
    }

    const init: RequestInit = {
      method: def.method,
      headers: { Accept: 'text/event-stream' },
      signal: args.signal,
    };
    if (args.body !== undefined) {
      init.headers = { ...init.headers, 'Content-Type': 'application/json' };
      init.body = JSON.stringify(args.body);
    }

    const res = await fetch(url.toString(), init);
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');
      throw new ApiCallError(res.status, text || 'stream_failed');
    }

    reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line ("\n\n").
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const frame = parseSseFrame<TOutput>(raw);
        if (!frame) continue;
        // Framework error frames use SSE's `event:` channel — throw so
        // the consumer's gracefulFallback / catch handles it. Routes'
        // typed event union stays free of framework concerns.
        if (frame.event === 'error') {
          throw new StreamError(typeof frame.data === 'string' ? frame.data : 'internal_error');
        }
        yield frame;
      }
    }
  } finally {
    reader?.releaseLock();
  }
}

async function* streamGen<TParams, TQuery, TBody, TOutput>(
  def: RouteDef<TParams, TQuery, TBody, TOutput>,
  args: CallArgs<TParams, TQuery, TBody>,
): AsyncGenerator<SseEvent<TOutput>, void, void> {
  try {
    for await (const frame of rawStreamGen(def, args, null)) {
      yield { event: frame.event, data: frame.data };
    }
  } catch (err) {
    const loginPath = loginPathForSessionExpiry(err);
    if (loginPath) {
      bounceToLogin(loginPath);
      throw new AbortError('unauthenticated');
    }
    runGracefulFallback(args.gracefulFallback, err);
    throw err;
  }
}

// Synthetic gap event the resumable wrapper injects when a reconnect
// detects in-process restart. Bus-forwarding routes emit their own gap
// (with a real count) server-side. Callers using `resumable` MUST
// include `{ kind: 'gap', count: number | null }` in their TOutput
// union — the type is asserted, not parsed.
function gapEvent<TOutput>(count: number | null): SseEvent<TOutput> {
  return { event: null, data: { kind: 'gap', count } as TOutput };
}

async function* streamGenResumable<TParams, TQuery, TBody, TOutput>(
  def: RouteDef<TParams, TQuery, TBody, TOutput>,
  args: CallArgs<TParams, TQuery, TBody>,
  resume: ResumableOptions,
): AsyncGenerator<SseEvent<TOutput>, void, void> {
  const maxAttempts = resume.maxAttempts ?? 5;
  const initialBackoff = resume.backoffMs ?? 1000;
  let lastEventI = 0;
  let attempt = 0;
  let reconnecting = false;

  try {
    while (true) {
      const extraQuery = reconnecting ? { since: String(lastEventI) } : null;
      let firstFrameThisRun = true;
      try {
        for await (const frame of rawStreamGen(def, args, extraQuery)) {
          if (firstFrameThisRun) {
            firstFrameThisRun = false;
            if (reconnecting && frame.i !== null && frame.i <= lastEventI) {
              // Server restarted (in-process route re-ran the handler) — its
              // first frame id is 1 but client's lastEventI is N>0. All
              // events between are lost. Inject a synthetic gap. Bus routes
              // detect their own gaps server-side and emit a real gap frame
              // with a count; that frame's id > lastEventI so this branch
              // doesn't fire for them.
              yield gapEvent<TOutput>(null);
            }
            // Reset reconnecting now that we've seen at least one frame.
            reconnecting = false;
            attempt = 0;
          }
          if (frame.i !== null && frame.i > lastEventI) lastEventI = frame.i;
          yield { event: frame.event, data: frame.data };
        }
        // Clean end of stream.
        return;
      } catch (err) {
        if (AbortError.is(err)) throw err;
        // No session — bounce to login rather than retrying a guaranteed 401.
        const loginPath = loginPathForSessionExpiry(err);
        if (loginPath) {
          bounceToLogin(loginPath);
          throw new AbortError('unauthenticated');
        }
        // Framework error from the server — retrying would just hit the
        // same exception. Surface immediately so gracefulFallback fires.
        if (err instanceof StreamError) throw err;
        attempt += 1;
        if (attempt > maxAttempts) throw err;
        reconnecting = true;
        const wait = initialBackoff * Math.pow(1.5, attempt - 1);
        await sleep(wait, args.signal);
      }
    }
  } catch (err) {
    const loginPath = loginPathForSessionExpiry(err);
    if (loginPath) {
      bounceToLogin(loginPath);
      throw new AbortError('unauthenticated');
    }
    runGracefulFallback(args.gracefulFallback, err);
    throw err;
  }
}

function sleep(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AbortError('aborted'));
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(t);
      reject(new AbortError('aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

// Build the same URL `callApi` would hit, but without firing a request.
// Useful for `<a href>` and `<img src>` against binary routes.
export function apiUrl<TParams, TQuery>(
  def: RouteDef<TParams, TQuery, unknown, unknown>,
  args: { params?: TParams; query?: TQuery } = {},
): string {
  const url = new URL(
    buildUrl(def.path, args.params as Record<string, unknown> | undefined),
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
  );
  if (args.query) {
    for (const [k, v] of Object.entries(args.query)) {
      if (v == null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + url.search;
}

function parseSseFrame<T>(raw: string): ParsedFrame<T> | null {
  let event: string | null = null;
  let i: number | null = null;
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue; // comments + heartbeats
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
    else if (field === 'id') {
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) i = parsed;
    }
  }
  if (dataLines.length === 0) return null;
  const text = dataLines.join('\n');
  const data: T = parseJsonSafe<T>(text) ?? (text as T);
  return { event, data, i };
}
